import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React, { Component } from 'react';

import { searchExercises } from '../../api/exercises';
import { ExerciseSearchFilters, ExerciseSearchItem } from '../../api/exercises.interface';
import ExerciseSearch from '../exercises/ExerciseSearch';

interface Props {
    onCancel: () => void;
    onConfirm: (items: ExerciseSearchItem[]) => void;
    selectedItems: ExerciseSearchItem[];
    title: string;
    visible: boolean;
}

interface State {
    currentFilters: ExerciseSearchFilters;
    hasLoadedSearch: boolean;
    items: ExerciseSearchItem[];
    loadingList: boolean;
    pendingSelectedItems: ExerciseSearchItem[];
}

class ProgramTagExerciseSelectorDialog extends Component<Props, State> {
    state: State = {
        currentFilters: {},
        hasLoadedSearch: false,
        items: [],
        loadingList: false,
        pendingSelectedItems: [],
    };

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.visible && this.props.visible) {
            this.setState({
                currentFilters: {},
                hasLoadedSearch: false,
                items: [],
                loadingList: false,
                pendingSelectedItems: this.props.selectedItems,
            });
        }
    }

    onConfirmSelections = () => {
        this.props.onConfirm(this.state.pendingSelectedItems);
    };

    onIgnoreExerciseAction = () => {
        return undefined;
    };

    onRemoveSelection = (tag: number) => {
        this.setState((currentState) => ({
            pendingSelectedItems: currentState.pendingSelectedItems.filter((item) => item.tag !== tag),
        }));
    };

    onResetSearch = () => {
        this.setState({
            currentFilters: {},
            hasLoadedSearch: false,
            items: [],
            loadingList: false,
        });
    };

    onSearch = async (filters: ExerciseSearchFilters) => {
        this.setState({ currentFilters: filters, loadingList: true });

        try {
            const items = await searchExercises(filters);

            this.setState({ hasLoadedSearch: true, items, loadingList: false });
        } catch {
            this.setState({ hasLoadedSearch: true, items: [], loadingList: false });
        }
    };

    onSelectExercise = (item: ExerciseSearchItem) => {
        this.setState((currentState) => {
            if (currentState.pendingSelectedItems.some((selectedItem) => selectedItem.tag === item.tag)) {
                return currentState;
            }

            return {
                pendingSelectedItems: [...currentState.pendingSelectedItems, item],
            };
        });
    };

    render() {
        const { onCancel, title, visible } = this.props;
        const { currentFilters, hasLoadedSearch, items, loadingList, pendingSelectedItems } = this.state;

        return (
            <Dialog fullWidth maxWidth="lg" open={visible}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700 }} variant="subtitle2">Selected</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {pendingSelectedItems.map((item) => (
                                <Chip key={item.tag} label={`${item.exercise} (${item.tag})`} onDelete={() => this.onRemoveSelection(item.tag)} />
                            ))}
                            {!pendingSelectedItems.length && (
                                <Typography color="text.secondary" variant="body2">No exercises selected yet.</Typography>
                            )}
                        </Box>
                    </Box>
                    <ExerciseSearch
                        currentFilters={currentFilters}
                        hasLoadedSearch={hasLoadedSearch}
                        items={items}
                        loadingList={loadingList}
                        onDeleteExercise={this.onIgnoreExerciseAction}
                        onEditExercise={this.onIgnoreExerciseAction}
                        onReset={this.onResetSearch}
                        onSearch={this.onSearch}
                        onSelectExercise={this.onSelectExercise}
                        selectedExerciseTags={pendingSelectedItems.map((item) => item.tag)}
                        selectionMode="multiple"
                        submitLabel="Search Exercises"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button onClick={this.onConfirmSelections} variant="contained">Use Selections</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default ProgramTagExerciseSelectorDialog;